import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Copy, Check, ArrowRight, ExternalLink } from "lucide-react";

const PLATFORM_LINKS = {
  openai:     "https://chat.openai.com/",
  anthropic:  "https://claude.ai/",
  gemini:     "https://gemini.google.com/",
  perplexity: "https://www.perplexity.ai/",
};

function CopyPromptButton({ text, testId }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text || "");
          setCopied(true);
          setTimeout(() => setCopied(false), 1800);
        } catch {}
      }}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 font-mono-eav text-[11px] tracking-wider uppercase px-3 py-1.5 rounded-sm transition-colors"
      style={{
        background: copied ? "rgba(109, 182, 109, 0.15)" : "rgba(184, 150, 62, 0.12)",
        color: copied ? "#3f8a3f" : "#8e7126",
        border: `1px solid ${copied ? "#6db66d" : "#b8963e"}`,
      }}
    >
      {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy prompt</>}
    </button>
  );
}

export default function ManualRun() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [activeTab, setActiveTab] = useState({ A: "openai", B: "openai", C: "openai" });
  const [outputs, setOutputs] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/audits/${id}/prompts`);
        setData(data);
        setOutputs(data.manual_outputs || {});
      } catch (e) {
        setErr(formatApiError(e));
      }
    })();
  }, [id]);

  const updateField = (platform, key, value) => {
    setOutputs((prev) => ({
      ...prev,
      [platform]: { ...(prev[platform] || {}), [key]: value },
    }));
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await api.put(`/audits/${id}/manual-outputs`, { outputs });
      setSavedAt(new Date());
    } catch (e) {
      alert(formatApiError(e));
    }
    setSaving(false);
  };

  const continueToScoring = async () => {
    await saveAll();
    navigate(`/audit/${id}/score`);
  };

  if (err) return <div className="max-w-3xl mx-auto p-12 text-[#c46a5a]" data-testid="manualrun-error">{err}</div>;
  if (!data) return <div className="max-w-3xl mx-auto p-12 text-[#c9bca4] font-mono-eav text-sm" data-testid="manualrun-loading">Loading…</div>;

  const { prompts, platforms, audit } = data;
  const totalFilled = platforms.reduce((acc, p) => {
    const block = outputs[p.key] || {};
    return acc + ["A", "B", "C"].filter((pid) => (block[pid] || "").trim()).length;
  }, 0);
  const totalSlots = platforms.length * 3;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="manualrun-page">
      <div className="flex flex-wrap justify-between gap-3 mb-6">
        <Link to="/dashboard" data-testid="manualrun-back" className="text-sm text-[#8a7e6d] hover:text-[#b8963e] transition-colors font-mono-eav tracking-wider">
          ← BACK TO DASHBOARD
        </Link>
        <div className="flex items-center gap-2">
          {savedAt && (
            <span className="font-mono-eav text-[10px] tracking-wider text-[#6db66d]">
              ● SAVED {savedAt.toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})}
            </span>
          )}
          <button onClick={saveAll} disabled={saving} data-testid="manualrun-save"
                  className="btn-ghost text-sm py-2">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <p className="overline">Manual Run · Step 1 of 2</p>
      <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl text-[#f4efe6] leading-[1.05]" style={{fontWeight: 700}}>
        Copy the prompts. <em className="text-[#b8963e] italic font-normal">Paste the outputs.</em>
      </h1>
      <p className="mt-3 text-[#c9bca4] max-w-2xl">
        Run each prompt in ChatGPT, Gemini, Claude, and Perplexity. Paste the responses below. Notes are optional but help when scoring.
      </p>
      <p className="mt-3 font-mono-eav text-[11px] tracking-wider text-[#5e5448]">
        {audit.company_name.toUpperCase()} · {audit.industry.toUpperCase()} · {audit.region.toUpperCase()}
      </p>

      {/* Progress */}
      <div className="mt-8 eav-card p-4 flex items-center justify-between" data-testid="manualrun-progress">
        <div>
          <p className="overline-soft">Progress</p>
          <p className="mt-1 font-serif-display text-2xl text-[#f4efe6]">
            <span className="font-bold">{totalFilled}</span><span className="text-[#5e5448] text-sm font-mono-eav">/{totalSlots} filled</span>
          </p>
        </div>
        <div className="flex-1 mx-6 max-w-md">
          <div className="h-1.5 bg-[#1a1815] overflow-hidden">
            <div className="h-full bg-[#b8963e] transition-all duration-500"
                 style={{ width: `${Math.round((totalFilled / totalSlots) * 100)}%` }} />
          </div>
        </div>
      </div>

      {/* Prompts */}
      <div className="mt-8 space-y-6">
        {prompts.map((prompt) => (
          <section key={prompt.id} data-testid={`manualrun-prompt-${prompt.id}`} className="eav-card overflow-hidden">
            {/* Prompt header */}
            <div className="px-6 py-5 border-b border-[#2a2620]" style={{ background: "#0a0908" }}>
              <p className="overline">Prompt {prompt.id} · {prompt.label}</p>
              <div className="mt-3 flex items-start justify-between gap-3">
                <p className="font-serif-display text-[17px] text-[#f4efe6] leading-snug flex-1" data-testid={`prompt-text-${prompt.id}`}>
                  "{prompt.text}"
                </p>
                <CopyPromptButton text={prompt.text} testId={`copy-prompt-${prompt.id}`} />
              </div>
            </div>

            {/* Platform tabs */}
            <div className="flex border-b border-[#2a2620]" data-testid={`tabs-${prompt.id}`}>
              {platforms.map((plat) => {
                const isActive = activeTab[prompt.id] === plat.key;
                const filled = ((outputs[plat.key] || {})[prompt.id] || "").trim();
                return (
                  <button
                    key={plat.key}
                    onClick={() => setActiveTab({ ...activeTab, [prompt.id]: plat.key })}
                    data-testid={`tab-${prompt.id}-${plat.key}`}
                    className="relative flex-1 px-4 py-3 font-mono-eav text-xs tracking-wider uppercase transition-colors"
                    style={{
                      color: isActive ? "#b8963e" : "#8a7e6d",
                      background: isActive ? "rgba(184, 150, 62, 0.06)" : "transparent",
                      borderBottom: isActive ? "2px solid #b8963e" : "2px solid transparent",
                    }}
                  >
                    {filled && <span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-[#6db66d]" />}
                    {plat.label}
                  </button>
                );
              })}
            </div>

            {/* Active platform content */}
            {(() => {
              const platKey = activeTab[prompt.id];
              const platLabel = platforms.find((p) => p.key === platKey)?.label;
              const block = outputs[platKey] || {};
              return (
                <div className="px-6 py-5" data-testid={`tab-content-${prompt.id}-${platKey}`}>
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <CopyPromptButton text={prompt.text} testId={`copy-tabprompt-${prompt.id}-${platKey}`} />
                      <a href={PLATFORM_LINKS[platKey]} target="_blank" rel="noreferrer"
                         data-testid={`open-${platKey}`}
                         className="inline-flex items-center gap-1.5 text-xs text-[#8a7e6d] hover:text-[#b8963e] transition-colors font-mono-eav tracking-wider">
                        OPEN {platLabel?.toUpperCase()} <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                  <label className="overline-soft">Paste {platLabel} output</label>
                  <textarea
                    rows={8}
                    data-testid={`output-${prompt.id}-${platKey}`}
                    value={block[prompt.id] || ""}
                    onChange={(e) => updateField(platKey, prompt.id, e.target.value)}
                    placeholder={`Paste ${platLabel}'s response to Prompt ${prompt.id} here…`}
                    className="mt-2 eav-input font-mono-eav text-sm"
                    style={{ lineHeight: 1.6 }}
                  />
                  <label className="overline-soft mt-4 block">Notes (optional)</label>
                  <textarea
                    rows={2}
                    data-testid={`notes-${prompt.id}-${platKey}`}
                    value={block[`notes_${prompt.id}`] || ""}
                    onChange={(e) => updateField(platKey, `notes_${prompt.id}`, e.target.value)}
                    placeholder="Anything interesting about this response…"
                    className="mt-2 eav-input"
                  />
                </div>
              );
            })()}
          </section>
        ))}
      </div>

      <div className="mt-10 flex items-center justify-between flex-wrap gap-3">
        <p className="font-mono-eav text-[11px] tracking-wider text-[#5e5448]">
          AT LEAST 1 OUTPUT REQUIRED TO SCORE · NOTES ARE OPTIONAL
        </p>
        <button onClick={continueToScoring} disabled={saving || totalFilled === 0}
                data-testid="manualrun-continue"
                className="btn-gold">
          Continue to Scoring <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
